import { TextInput } from '@ardoq/forms';
import { useState } from 'react';

export type AxisHeaderTextInputProps = {
  'aria-label': string;
  dataTestId: string;
  placeholder: string;
  isDisabled: boolean;
};

type AxisHeaderInputProps = {
  inputProps: AxisHeaderTextInputProps;
  persistedValue: string | null;
  defaultValue: string;
  onPersist: (value: string | null) => void;
  helperText: string;
  inputRef: React.Ref<React.ComponentRef<typeof TextInput>>;
  onKeyDown: (e: React.KeyboardEvent) => void;
};

type HeaderInputDraft = {
  persistedValue: string | null;
  defaultValue: string;
  localValue: string;
};

const toHeaderInputDraft = (
  persistedValue: string | null,
  defaultValue: string
): HeaderInputDraft => ({
  persistedValue,
  defaultValue,
  localValue: persistedValue ?? defaultValue,
});

export const useHeaderInputLocalState = (
  persistedValue: string | null,
  defaultValue: string,
  onPersist: (value: string | null) => void
) => {
  const [draft, setDraft] = useState(() =>
    toHeaderInputDraft(persistedValue, defaultValue)
  );

  if (
    draft.persistedValue !== persistedValue ||
    draft.defaultValue !== defaultValue
  ) {
    setDraft(toHeaderInputDraft(persistedValue, defaultValue));
  }

  const setLocalValue = (localValue: string) =>
    setDraft(current => ({ ...current, localValue }));

  return {
    localValue: draft.localValue,
    onLocalValueChange: (value: string | null) => {
      setLocalValue(value ?? '');
    },
    onBlur: () => {
      const trimmed = draft.localValue.trim();
      const normalizedDefault = defaultValue.trim();

      onPersist(
        trimmed === '' || trimmed === normalizedDefault ? null : trimmed
      );
      setLocalValue(trimmed === '' ? defaultValue : trimmed);
    },
  };
};

export const AxisHeaderInput = ({
  inputProps,
  persistedValue,
  defaultValue,
  onPersist,
  helperText,
  inputRef,
  onKeyDown,
}: AxisHeaderInputProps) => {
  const { localValue, onLocalValueChange, onBlur } = useHeaderInputLocalState(
    persistedValue,
    defaultValue,
    onPersist
  );

  return (
    <TextInput
      {...inputProps}
      ref={inputRef}
      onKeyDown={onKeyDown}
      helperText={helperText}
      value={inputProps.isDisabled ? '' : localValue}
      onValueChange={onLocalValueChange}
      onBlur={onBlur}
    />
  );
};
